import type {
    CreateSellerInput,
    SearchDirection,
    SellerSearchOrderByInput,
    UpdateSellerAccountInput,
    UpdateSellerInput,
} from '@/graphql/graphql';
import {
    CREATE_SELLER,
    GET_SELLER,
    GET_SELLERS,
    GET_SELLER_ACCOUNT,
    GET_SELLER_SUMMARY,
    UPDATE_SELLER,
    UPDATE_SELLER_ACCOUNT,
    WITHDRAW_SELLER_WALLET,
} from '@/graphql/queries/sellers.graphql';
import { apolloClient } from './apollo-client';

const sellersSortFieldMap: Record<string, keyof SellerSearchOrderByInput> = {
    sku: 'sku',
    legalName: 'legalName',
    contactName: 'contactName',
    contactEmail: 'contactEmail',
};

export type FetchSellersListArgs = {
    page: number;
    pageSize: number;
    filters?: {
        [key: string]: unknown;
    };
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
};

export const fetchSellersList = async ({ page, pageSize, filters, sortBy, sortOrder }: FetchSellersListArgs) => {
    const orderBy: SellerSearchOrderByInput = {};

    if (sortBy && sellersSortFieldMap[sortBy]) {
        orderBy[sellersSortFieldMap[sortBy]] = sortOrder?.toUpperCase() as SearchDirection;
    }

    const response = await apolloClient.query({
        query: GET_SELLERS,
        variables: {
            filter: {
                contactEmail: filters?.contactEmail as string | undefined,
                contactName: filters?.contactName as string | undefined,
                sku: filters?.sku ? Number(filters.sku) : undefined,
                legalName: filters?.legalName as string | undefined,
            },
            orderBy,
            pagination: {
                limit: pageSize,
                offset: (page - 1) * pageSize,
            },
        },
    });

    return {
        data: response.data.sellers.backoffice.items,
        total: response.data.sellers.backoffice.count,
    };
};

export const updateSeller = async (id: string, input: UpdateSellerInput) => {
    const normalizedInput = {
        ...input,
        contactEmail: input.contactEmail?.toLocaleLowerCase(),
        billingEmail: input.billingEmail?.toLocaleLowerCase(),
    };
    const result = await apolloClient.mutate({
        mutation: UPDATE_SELLER,
        variables: { id, input: normalizedInput },
    });
    return { data: result };
};

export const updateSellerAccount = async (input: UpdateSellerAccountInput) => {
    const normalizedInput = {
        ...input,
        contactEmail: input.contactEmail?.toLocaleLowerCase(),
        billingEmail: input.billingEmail?.toLocaleLowerCase(),
    };
    const result = await apolloClient.mutate({
        mutation: UPDATE_SELLER_ACCOUNT,
        variables: { input: normalizedInput },
    });
    return { data: result };
};

export const createSeller = async (input: CreateSellerInput) => {
    const normalizedInput = {
        ...input,
        contactEmail: input.contactEmail.toLocaleLowerCase(),
        billingEmail: input.billingEmail.toLocaleLowerCase(),
    };
    const result = await apolloClient.mutate({
        mutation: CREATE_SELLER,
        variables: { input: normalizedInput },
    });
    return { data: result };
};

export const getSeller = async (id: string, full = false) => {
    const response = await apolloClient.query({
        query: full ? GET_SELLER : GET_SELLER_SUMMARY,
        variables: { id },
    });
    return response.data.seller.backoffice;
};

export const getSellerAccount = async (id: string) => {
    const response = await apolloClient.query({
        query: GET_SELLER_ACCOUNT,
        variables: { id },
    });
    return response.data.seller.account;
};

export const withdrawSellerWallet = async (sellerId: string) => {
    const result = await apolloClient.mutate({
        mutation: WITHDRAW_SELLER_WALLET,
        variables: { sellerId },
    });
    return result.data;
};
